<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue';
import type { AllocGroupIndex, AllocPhaseIndex } from '../../models/SolshareSystemSpec';
import AllocationsPerPhaseLayout from './AllocationsPerPhaseLayout.vue';
import { ALGORITHM_TYPE } from '@/utils/constants';
import type { AllocPerPhase } from '../../models/SolshareSystemSpec';

const ALLOCATION_COMMIT_DEBOUNCE_MS = 2000;

const props = defineProps<{
  phaseIndex: AllocPhaseIndex;
  phaseAllocations: AllocPerPhase;
  algorithmType: ALGORITHM_TYPE;
  showAsKwp: boolean;
  kwpPerPhase: number;
}>();

const emit = defineEmits<{
  (
    e: 'updateAllocationValue',
    phaseIndex: AllocPhaseIndex,
    groupIndex: AllocGroupIndex,
    value: number,
  ): void;
}>();

const groupIndexes = computed(() => {
  return Object.keys(props.phaseAllocations).map((key) => {
    return Number(key) as AllocGroupIndex;
  });
});

const isCustomAllocation = computed(() => {
  return props.algorithmType === ALGORITHM_TYPE.CUSTOM_ALLOCATION;
});

const terminalNotConnected = (groupIndex: AllocGroupIndex) => {
  return !props.phaseAllocations[groupIndex]?.connected;
};

const allocationsRemainingValue = computed(() => {
  const allocated = Object.values(props.phaseAllocations).reduce(
    (acc, item) => (acc as number) + (item.allocationValue as number),
    0,
  );

  if (props.showAsKwp) {
    return Number((props.kwpPerPhase - (allocated as number)).toFixed(2));
  } else {
    return Number((100 - (allocated as number)).toFixed(2));
  }
});

const showError = computed(() => {
  return isCustomAllocation.value && allocationsRemainingValue.value !== 0;
});

/** Raw input while typing — avoids parseFloat on each keystroke eating decimals (e.g. 32. --> 32, then 8 --> 328). */
const editingAllocationText = ref<Partial<Record<AllocGroupIndex, string>>>({});
const commitTimers = new Map<AllocGroupIndex, ReturnType<typeof setTimeout>>();

const clearCommitTimer = (groupIndex: AllocGroupIndex) => {
  const timer = commitTimers.get(groupIndex);
  if (timer !== undefined) {
    clearTimeout(timer);
    commitTimers.delete(groupIndex);
  }
};

const allocationInputValue = (groupIndex: AllocGroupIndex) => {
  if (groupIndex in editingAllocationText.value) {
    return editingAllocationText.value[groupIndex];
  }
  const allocationValue = props.phaseAllocations[groupIndex]?.allocationValue;
  return allocationValue === undefined ? '' : String(allocationValue);
};

const commitAllocationValue = (groupIndex: AllocGroupIndex) => {
  if (!(groupIndex in editingAllocationText.value)) {
    return;
  }

  const raw = editingAllocationText.value[groupIndex] ?? '';
  const next = { ...editingAllocationText.value };
  delete next[groupIndex];
  editingAllocationText.value = next;

  const numericValue = raw === '' ? 0 : parseFloat(raw);
  emit(
    'updateAllocationValue',
    props.phaseIndex,
    groupIndex,
    isNaN(numericValue) ? 0 : numericValue,
  );
};

const scheduleCommitAllocationValue = (groupIndex: AllocGroupIndex) => {
  clearCommitTimer(groupIndex);
  commitTimers.set(
    groupIndex,
    setTimeout(() => commitAllocationValue(groupIndex), ALLOCATION_COMMIT_DEBOUNCE_MS),
  );
};

const handleAllocationInput = (groupIndex: AllocGroupIndex, value: string) => {
  editingAllocationText.value = { ...editingAllocationText.value, [groupIndex]: value };
  scheduleCommitAllocationValue(groupIndex);
};

const handleAllocationBlur = (groupIndex: AllocGroupIndex) => {
  clearCommitTimer(groupIndex);
  commitAllocationValue(groupIndex);
};

onUnmounted(() => {
  commitTimers.forEach((timer) => clearTimeout(timer));
  commitTimers.clear();
});

const allocationUnitSymbol = props.showAsKwp
  ? ' kWp' // Add space before the value, e.g. 4.30 kWp
  : '%'; // No space before the value, e.g. 93.30%
</script>

<template>
  <AllocationsPerPhaseLayout
    :phaseIndex="phaseIndex"
    :groupIndexes="groupIndexes"
    :phaseAllocations="phaseAllocations"
    :displayAllocationRemaining="isCustomAllocation"
    :allocationsRemainingValue="allocationsRemainingValue"
    :class="!isCustomAllocation ? 'demand-based' : ''"
    :showAsKwp="showAsKwp"
  >
    <template #rightCell="{ phaseIndex, groupIndex }">
      <template v-if="isCustomAllocation">
        <template v-if="terminalNotConnected(groupIndex)">
          <a-input
            disabled
            :value="'-'"
            size="large"
            class="allocation-input allocation-input__no-connection"
            data-tn="allocation-custom-no-connection"
          />
        </template>
        <template v-else>
          <a-input
            inputmode="decimal"
            :value="allocationInputValue(groupIndex)"
            @input="(e: Event) => handleAllocationInput(groupIndex, (e.target as HTMLInputElement).value)"
            @blur="() => handleAllocationBlur(groupIndex)"
            size="large"
            :data-tn="`allocation-input-L${phaseIndex}-${groupIndex}`"
            class="allocation-input"
          >
            <template #suffix>{{ allocationUnitSymbol }}</template>
          </a-input>
        </template>
      </template>
      <template v-else>
        <template v-if="terminalNotConnected(groupIndex)">
          <span data-tn="allocation-demand-not-connected"> - </span>
        </template>
        <template v-else>
          <div class="demand-allocation-text">Demand</div>
        </template>
      </template>
    </template>
    <template #errorBlock v-if="showError">
      {{
        showAsKwp
          ? `Total allocation must equal total kWp per phase (${kwpPerPhase} kWp)`
          : 'Remaining value must be 0%'
      }}
    </template>
  </AllocationsPerPhaseLayout>
</template>

<style lang="less" scoped>
.allocation-input {
  :deep(.ant-input),
  :deep(.ant-input-affix-wrapper) {
    background-color: @color-background-light;
    color: @color-primary;
    text-align: center;
  }
}

.allocation-input__no-connection {
  border-color: @color-primary;

  &:disabled {
    border-color: @color-primary;
  }

  :deep(&.ant-input[disabled]:hover),
  :deep(&.ant-input[disabled]:active),
  :deep(&.ant-input[disabled]:focus) {
    border-color: @color-primary;
  }

  :deep(&.ant-input),
  :deep(&.ant-input-affix-wrapper),
  :deep(&.ant-input-suffix) {
    background-color: @color-bg-no-connection;
    color: @color-secondary;
    font-style: italic;
    font-size: 1rem;
    text-align: center;
  }
}

.demand-allocation-text {
  font-size: 0.8rem;
  font-weight: @font-weight-medium;
}

.demand-based {
  :deep(.right-cell-container) {
    border-top: 1px solid @color-mid-tone;

    &.last-row {
      border-bottom: 1px solid @color-mid-tone;
    }
  }
}
</style>
